import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  ArcElement,
  BarElement,
  Filler,
} from 'chart.js';
import { DashboardData, CalculatedData } from './types';
import SettingsPanel from './components/SettingsPanel';
import Charts from './components/Charts';
import MetricsCards from './components/MetricsCards';
import { CompanyRegistrationModal } from './components/CompanyRegistrationModal';
import { calculateDashboardData } from './utils';
import {
  saveCustomerData,
  saveContactPerson,
  saveCustomerSession,
  updateContactPerson,
  updateCustomerData,
  findCustomerByCorporateNumber,
  findCustomerByNameWithoutCorporateNumber,
  findContactPersonByCompanyIdAndEmail,
} from './firebase/firestore';
import { CustomerData, ContactPerson, CustomerSession } from './firebase/types';

// Functions呼び出しの型定義
interface CalculateDashboardResponse {
  success: boolean;
  data?: CalculatedData;
  error?: string;
}

// Chart.jsの登録
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  ArcElement,
  BarElement,
  Filler
);

const NewDashboard: React.FC = () => {
  // 画面状態: 常に'dashboard'を表示
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 会社情報と担当者情報
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [contactPersonId, setContactPersonId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState('');

  // ダッシュボードデータ（デフォルト値）
  const defaultData: DashboardData = {
    companyName: '',
    generalHiring: 50,
    techHiring: 30,
    budget: 500,
    companySize: 0.6,
    industry: 1.2,
    salary: 25.0,
    stability: 4.2,
    growth: 4.0,
    organicRatio: 42,
    snsFollowers: 8500,
    pagePV: 8000,
    rating: 5.0,
    brandVisibility: 3.2,
    effectiveJobSeekingRate: 45.8,
    prMeasures: 4,
    regionalFactor: 1.0,
    workLifeBalance: 3.0,
    technicalCapability: 3.0,
  };

  const [data, setData] = useState<DashboardData>(defaultData);

  // デフォルトの入力値から計算した値を初期値として設定
  const [calculatedData, setCalculatedData] = useState<CalculatedData>(
    calculateDashboardData(defaultData)
  );
  const [calculationError, setCalculationError] = useState('');
  const [lastInputType, setLastInputType] = useState<
    'slider' | 'input' | 'select'
  >('input');

  // データ保存用のref（スライダーのonChangeEndを検知するため）
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstSaveRef = useRef<boolean>(true);

  // calculatedDataの最新値を保持するためのref
  const calculatedDataRef = useRef<CalculatedData | null>(null);
  // 前回保存したcalculatedDataのfinalRatioを保持（重複保存を防ぐため）
  const lastSavedFinalRatioRef = useRef<number | null>(null);

  useEffect(() => {
    calculatedDataRef.current = calculatedData;
  }, [calculatedData]);

  // saveSessionDataの最新版を保持するためのref
  const saveSessionDataRef = useRef<(() => Promise<void>) | undefined>(
    undefined
  );

  // データ保存処理（customerSessionsに保存）
  const saveSessionData = useCallback(async () => {
    if (!companyId || !contactPersonId) return;

    // 最新のcalculatedDataを参照
    const currentCalculatedData = calculatedDataRef.current;
    if (!currentCalculatedData) {
      // eslint-disable-next-line no-console
      console.log(
        '[saveSessionData] calculatedDataがまだないため、保存をスキップ'
      );
      return;
    }

    try {
      const customerData: CustomerData = {
        companyName: data.companyName,
        generalHiring: data.generalHiring,
        techHiring: data.techHiring,
        budget: data.budget,
        companySize: data.companySize,
        industry: data.industry,
        salary: data.salary,
        stability: data.stability,
        growth: data.growth,
        organicRatio: data.organicRatio,
        snsFollowers: data.snsFollowers,
        pagePV: data.pagePV,
        rating: data.rating,
        brandVisibility: currentCalculatedData.brandVisibility || 0,
        effectiveJobSeekingRate:
          currentCalculatedData.effectiveJobSeekingRate || 0,
        prMeasures: data.prMeasures,
        regionalFactor: data.regionalFactor,
        workLifeBalance: data.workLifeBalance,
        technicalCapability: data.technicalCapability,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const sessionData: Omit<
        CustomerSession,
        'id' | 'createdAt' | 'updatedAt'
      > = {
        companyId,
        contactPersonId,
        customerData,
        calculatedResult: {
          finalRatio: currentCalculatedData.finalRatio,
          difficulty: currentCalculatedData.difficulty,
          difficultyClass: currentCalculatedData.difficultyClass,
          requiredApplicants: currentCalculatedData.requiredApplicants,
          costPerHire: currentCalculatedData.costPerHire,
        },
        savedAt: new Date(),
      };

      // eslint-disable-next-line no-console
      console.log('[saveSessionData] セッションデータを保存中...');
      await saveCustomerSession(sessionData);
      // eslint-disable-next-line no-console
      console.log('[saveSessionData] セッションデータ保存完了');

      // 初回保存時のみ、担当者のfirstInputAtを更新
      if (isFirstSaveRef.current) {
        isFirstSaveRef.current = false;
        if (contactPersonId) {
          // eslint-disable-next-line no-console
          console.log('[saveSessionData] 初回保存: 担当者情報を更新中...');
          await updateContactPerson(contactPersonId, {
            firstInputAt: new Date(),
            lastInputAt: new Date(),
          });
          // eslint-disable-next-line no-console
          console.log('[saveSessionData] 担当者情報更新完了');
        }
      } else {
        // 2回目以降は、lastInputAtのみ更新
        if (contactPersonId) {
          // eslint-disable-next-line no-console
          console.log('[saveSessionData] 2回目以降: 担当者情報を更新中...');
          await updateContactPerson(contactPersonId, {
            lastInputAt: new Date(),
          });
          // eslint-disable-next-line no-console
          console.log('[saveSessionData] 担当者情報更新完了');
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[saveSessionData] データ保存エラー:', error);

      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.error('[saveSessionData] エラー詳細:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
        });
      }
    }
  }, [companyId, contactPersonId, data]); // calculatedDataを依存配列から削除

  // saveSessionDataの最新版をrefに保存
  useEffect(() => {
    saveSessionDataRef.current = saveSessionData;
  }, [saveSessionData]);

  // Functions呼び出しで計算ロジック（デバウンス付き）
  useEffect(() => {
    // 会社情報登録完了前は計算を実行しない
    if (!companyId || !contactPersonId) {
      return;
    }

    // スライダーの場合は計算をスキップ（onSliderChangeEndで実行されるため）
    if (lastInputType === 'slider') {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('useEffect: 計算を開始します', {
      companyId,
      contactPersonId,
      dataCompanyName: data.companyName,
      lastInputType,
    });

    const calculateWithFunctions = async () => {
      setCalculationError('');

      try {
        const response = await fetch(
          'https://calculatedashboard-npl66qeuna-uc.a.run.app',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = (await response.json()) as CalculateDashboardResponse;

        if (result.success && result.data) {
          // eslint-disable-next-line no-console
          console.log('計算完了:', result.data);
          setCalculatedData(result.data);
          // 初回保存は別のuseEffectで処理（無限ループを防ぐため）
        } else {
          setCalculationError(result.error || '計算に失敗しました');
        }
      } catch (error) {
        // より詳細なエラーメッセージ
        let errorMessage = 'サーバーとの通信に失敗しました';
        if (error instanceof Error) {
          if (error.message.includes('functions/not-found')) {
            errorMessage =
              'Functionsが見つかりません。デプロイを確認してください。';
          } else if (error.message.includes('permission-denied')) {
            errorMessage = 'アクセス権限がありません。';
          } else if (error.message.includes('unavailable')) {
            errorMessage =
              'サーバーが利用できません。しばらく待ってから再試行してください。';
          } else {
            errorMessage = `エラー: ${error.message}`;
          }
        }

        setCalculationError(errorMessage);
      }
    };

    // デバウンス: 入力タイプに応じて時間を調整
    // スライダーの場合はonSliderChangeEndで計算が実行されるため、ここでは500msで統一
    const debounceTime = 500;
    const timeoutId = setTimeout(calculateWithFunctions, debounceTime);

    return () => clearTimeout(timeoutId);
  }, [data, lastInputType, companyId, contactPersonId]);

  // 計算完了後の自動保存処理（初回保存 + 入力変更時の保存）
  useEffect(() => {
    if (!companyId || !contactPersonId || !calculatedData) {
      return;
    }

    // 重複保存を防ぐ: 同じfinalRatioの場合は保存をスキップ
    if (lastSavedFinalRatioRef.current === calculatedData.finalRatio) {
      return;
    }

    // 初回保存のみ実行（初回保存フラグをチェック）
    if (isFirstSaveRef.current) {
      isFirstSaveRef.current = false;
      // eslint-disable-next-line no-console
      console.log('初回保存を実行します', {
        companyId,
        contactPersonId,
      });
      const currentSaveSessionData = saveSessionDataRef.current;
      if (currentSaveSessionData) {
        currentSaveSessionData()
          .then(() => {
            // 保存成功後、finalRatioを記録
            lastSavedFinalRatioRef.current = calculatedData.finalRatio;
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.error('初回保存エラー:', error);
          });
      }
    } else {
      // 2回目以降: 入力変更時の自動保存（デバウンス付き）
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      const debounceTime = lastInputType === 'slider' ? 1500 : 500;
      saveTimeoutRef.current = setTimeout(() => {
        // eslint-disable-next-line no-console
        console.log('入力変更時の自動保存を実行します');
        const currentSaveSessionData = saveSessionDataRef.current;
        if (currentSaveSessionData) {
          currentSaveSessionData()
            .then(() => {
              // 保存成功後、finalRatioを記録
              lastSavedFinalRatioRef.current = calculatedData.finalRatio;
            })
            .catch(error => {
              // eslint-disable-next-line no-console
              console.error('自動保存エラー:', error);
            });
        }
      }, debounceTime);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [calculatedData, companyId, contactPersonId, lastInputType]); // saveSessionDataを依存配列から削除

  // 会社情報登録処理
  const handleCompanyRegister = async (registerData: {
    companyName: string;
    companyAddress?: string;
    corporateNumber?: string;
    contactPerson: {
      name: string;
      email: string;
      phone: string;
      position: string;
    };
  }) => {
    // eslint-disable-next-line no-console
    console.log('handleCompanyRegister: 開始', registerData);
    try {
      // 1. 企業同定: 既存の企業を検索
      let savedCompanyId: string;
      let existingCompany: CustomerData | null = null;

      if (registerData.corporateNumber) {
        // 法人番号がある場合: 法人番号で検索
        // eslint-disable-next-line no-console
        console.log('法人番号で企業を検索中...', registerData.corporateNumber);
        existingCompany = await findCustomerByCorporateNumber(
          registerData.corporateNumber
        );
        if (existingCompany?.id) {
          // eslint-disable-next-line no-console
          console.log(
            '既存の企業が見つかりました（法人番号）:',
            existingCompany.id
          );
          savedCompanyId = existingCompany.id;
        }
      } else {
        // 法人番号がない場合: 法人番号がない既存データの中で、会社名が完全一致するものを検索
        // eslint-disable-next-line no-console
        console.log(
          '会社名で企業を検索中（法人番号なし）...',
          registerData.companyName
        );
        existingCompany = await findCustomerByNameWithoutCorporateNumber(
          registerData.companyName
        );
        if (existingCompany?.id) {
          // eslint-disable-next-line no-console
          console.log(
            '既存の企業が見つかりました（会社名）:',
            existingCompany.id
          );
          savedCompanyId = existingCompany.id;
        }
      }

      if (!existingCompany || !existingCompany.id) {
        // 既存の企業が見つからない場合: 新規作成
        // eslint-disable-next-line no-console
        console.log('新規企業として登録します');
        const customerData: Omit<CustomerData, 'createdAt' | 'updatedAt'> = {
          companyName: registerData.companyName,
          companyAddress: registerData.companyAddress,
          corporateNumber: registerData.corporateNumber,
          generalHiring: data.generalHiring,
          techHiring: data.techHiring,
          budget: data.budget,
          companySize: data.companySize,
          industry: data.industry,
          salary: data.salary,
          stability: data.stability,
          growth: data.growth,
          organicRatio: data.organicRatio,
          snsFollowers: data.snsFollowers,
          pagePV: data.pagePV,
          rating: data.rating,
          brandVisibility: 0, // 計算結果のため、初期値は0
          effectiveJobSeekingRate: 0, // 計算結果のため、初期値は0
          prMeasures: data.prMeasures,
          regionalFactor: data.regionalFactor,
          workLifeBalance: data.workLifeBalance,
          technicalCapability: data.technicalCapability,
        };

        // eslint-disable-next-line no-console
        console.log('会社情報を保存中...', customerData);
        try {
          savedCompanyId = await saveCustomerData(customerData);
          // eslint-disable-next-line no-console
          console.log('会社情報保存完了:', savedCompanyId);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('会社情報保存エラー（詳細）:', error);
          throw error;
        }
      } else {
        // 既存の企業が見つかった場合: existingCompany.idが存在することが保証されている
        savedCompanyId = existingCompany.id;

        // 企業情報を更新（住所や法人番号が追加された場合）
        if (
          registerData.companyAddress &&
          registerData.companyAddress !== existingCompany.companyAddress
        ) {
          // eslint-disable-next-line no-console
          console.log('企業情報を更新中（住所追加）...');
          await updateCustomerData(savedCompanyId, {
            companyAddress: registerData.companyAddress,
          });
        }
        if (registerData.corporateNumber && !existingCompany.corporateNumber) {
          // eslint-disable-next-line no-console
          console.log('企業情報を更新中（法人番号追加）...');
          await updateCustomerData(savedCompanyId, {
            corporateNumber: registerData.corporateNumber,
          });
        }
      }

      setCompanyId(savedCompanyId);
      setCompanyName(registerData.companyName);
      setData(prev => ({ ...prev, companyName: registerData.companyName }));

      // 2. ユーザー（担当者）同定: 企業IDとemailで検索
      // eslint-disable-next-line no-console
      console.log('担当者を検索中（企業ID + email）...', {
        companyId: savedCompanyId,
        email: registerData.contactPerson.email,
      });
      let savedContactPersonId: string;
      const existingContactPerson = await findContactPersonByCompanyIdAndEmail(
        savedCompanyId,
        registerData.contactPerson.email
      );

      if (existingContactPerson?.id) {
        // 既存の担当者が見つかった場合: 既存のIDを使用し、lastInputAtを更新
        // eslint-disable-next-line no-console
        console.log('既存の担当者が見つかりました:', existingContactPerson.id);
        savedContactPersonId = existingContactPerson.id;

        // 担当者情報を更新（名前、電話番号、役職が変更された場合）
        const now = new Date();
        await updateContactPerson(savedContactPersonId, {
          name: registerData.contactPerson.name,
          phone: registerData.contactPerson.phone,
          position: registerData.contactPerson.position,
          lastInputAt: now,
        });
        // eslint-disable-next-line no-console
        console.log('担当者情報更新完了');
      } else {
        // 既存の担当者が見つからない場合: 新規作成
        // eslint-disable-next-line no-console
        console.log('新規担当者として登録します');
        const now = new Date();
        const contactPersonData: Omit<
          ContactPerson,
          'id' | 'createdAt' | 'updatedAt'
        > = {
          companyId: savedCompanyId,
          name: registerData.contactPerson.name,
          email: registerData.contactPerson.email,
          phone: registerData.contactPerson.phone,
          position: registerData.contactPerson.position,
          firstInputAt: now,
          lastInputAt: now,
        };

        // eslint-disable-next-line no-console
        console.log('担当者情報を保存中...', contactPersonData);
        savedContactPersonId = await saveContactPerson(contactPersonData);
        // eslint-disable-next-line no-console
        console.log('担当者情報保存完了:', savedContactPersonId);
      }

      setContactPersonId(savedContactPersonId);

      // 3. モーダルを閉じる
      setIsModalOpen(false);

      // 4. 初回保存フラグをリセット（保存処理を開始するため）
      isFirstSaveRef.current = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('会社情報登録エラー:', error);

      // より詳細なエラーメッセージを表示
      let errorMessage = '会社情報の登録に失敗しました。';
      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.error('エラー詳細:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
        });

        if (error.message.includes('permission-denied')) {
          errorMessage +=
            '\nアクセス権限がありません。Firestoreのセキュリティルールを確認してください。';
        } else if (error.message.includes('unavailable')) {
          errorMessage +=
            '\nFirestoreが利用できません。しばらく待ってから再試行してください。';
        } else if (error.message.includes('network')) {
          errorMessage +=
            '\nネットワークエラーが発生しました。接続を確認してください。';
        } else if (error.message.includes('timeout')) {
          errorMessage +=
            '\nタイムアウトが発生しました。しばらく待ってから再試行してください。';
        } else {
          errorMessage += `\nエラー: ${error.message}`;
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('予期しないエラー:', error);
        errorMessage += '\n予期しないエラーが発生しました。';
      }
      alert(errorMessage);
    }
  };

  const handleInputChange = (
    field: keyof DashboardData,
    value: string | number | boolean,
    inputType: 'slider' | 'input' | 'select' = 'input'
  ) => {
    setLastInputType(inputType);
    setData(prev => ({
      ...prev,
      [field]: value,
    }));
    // スライダーの場合は計算をスキップ（onSliderChangeEndで実行）
    // その他の入力の場合は計算が自動的に実行される（useEffectでdataが変更されるため）
  };

  // スライダーを離した時に計算を実行
  const handleSliderChangeEnd = (field: keyof DashboardData, value: number) => {
    // lastInputTypeを'input'に設定してからデータを更新
    // これでuseEffectがトリガーされ、計算が実行される
    setLastInputType('input');
    setData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    if (field === 'prMeasures') {
      const prCheckboxes = ['employeeRef', 'prVideo', 'snsActive', 'jobFair'];
      const currentCount = prCheckboxes.reduce((count, id) => {
        const checkbox = document.getElementById(id) as HTMLInputElement;
        return count + (checkbox?.checked ? 1 : 0);
      }, 0);
      const newCount = checked ? currentCount + 1 : currentCount - 1;
      setData(prev => ({ ...prev, prMeasures: newCount }));
    } else if (field === 'regionalFactor') {
      const regionCheckboxes = [
        'tokyo',
        'osaka',
        'aichi',
        'kanagawa',
        'fukuoka',
      ];
      const currentCount = regionCheckboxes.reduce((count, id) => {
        const checkbox = document.getElementById(id) as HTMLInputElement;
        return count + (checkbox?.checked ? 1 : 0);
      }, 0);
      const newCount = checked ? currentCount + 1 : currentCount - 1;
      const regionalFactor = 1.0 + (newCount - 1) * 0.1;
      setData(prev => ({ ...prev, regionalFactor }));
    }
  };

  // データ保存関数 - 2nd Phaseで使用予定
  /*
  const handleSaveData = async () => {
    if (!data.companyName.trim()) {
      setSaveMessage('会社名を入力してください');
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const customerData: CustomerData = {
        companyName: data.companyName,
        generalHiring: data.generalHiring,
        techHiring: data.techHiring,
        budget: data.budget,
        companySize: data.companySize,
        industry: data.industry,
        salary: data.salary,
        stability: data.stability,
        growth: data.growth,
        organicRatio: data.organicRatio,
        snsFollowers: data.snsFollowers,
        pagePV: data.pagePV,
        rating: data.rating,
        brandVisibility: calculatedData.brandVisibility || 0,
        effectiveJobSeekingRate: calculatedData.effectiveJobSeekingRate || 0,
        prMeasures: data.prMeasures,
        regionalFactor: data.regionalFactor,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Saving customer data:', customerData);
      const docId = await saveCustomerData(customerData);
      console.log('Data saved successfully with ID:', docId);
      setSaveMessage(`データを保存しました (ID: ${docId})`);
    } catch (error) {
      console.error('データ保存エラー:', error);
      setSaveMessage(`データの保存に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };
  */

  // ダッシュボード画面（常に表示）
  return (
    <div className="container mx-auto px-4 py-6">
      {/* ヘッダー */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">
          採用戦略分析ダッシュボード 2025
        </h1>
        <p className="text-xl text-white opacity-90">
          競合分析・ブランドスコア統合分析システム
        </p>
        {companyName && (
          <p className="text-lg text-white opacity-75 mt-2">
            企業: {companyName}
          </p>
        )}
      </div>

      {/* 会社名入力 - 2nd Phaseで有効化予定 */}
      {/* 
      <div className="dashboard-card p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">会社名</label>
        <input 
          type="text" 
          value={data.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg"
        />
        
        <div className="mt-4 flex items-center gap-4">
          <button
            onClick={handleSaveData}
            disabled={isSaving}
            className={`px-6 py-2 rounded-lg font-medium ${
              isSaving 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSaving ? '保存中...' : 'データを保存'}
          </button>
          
          {saveMessage && (
            <span className={`text-sm ${
              saveMessage.includes('失敗') ? 'text-red-600' : 'text-green-600'
            }`}>
              {saveMessage}
            </span>
          )}
        </div>
      </div>
      */}

      {calculationError && (
        <div className="dashboard-card p-6 mb-6 bg-red-50 border border-red-200">
          <div className="text-center">
            <div className="text-lg text-red-600">エラーが発生しました</div>
            <div className="text-sm text-red-500">{calculationError}</div>
          </div>
        </div>
      )}

      {/* メトリクスカード */}
      <MetricsCards data={data} calculatedData={calculatedData} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 設定パネル */}
        <SettingsPanel
          data={data}
          onInputChange={handleInputChange}
          onSliderChangeEnd={handleSliderChangeEnd}
          onCheckboxChange={handleCheckboxChange}
          onAnalysisClick={() => setIsModalOpen(true)}
          isAnalysisEnabled={!companyId || !contactPersonId}
        />

        {/* 分析結果 */}
        <Charts data={data} calculatedData={calculatedData} />
      </div>

      {/* フッター */}
      <div className="text-center mt-12 text-white opacity-75 text-sm">
        © 2025 採用可能母集団試算システム -
        データソース：厚生労働省・文部科学省統計データ
      </div>

      {/* 会社情報登録モーダル */}
      <CompanyRegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onRegister={handleCompanyRegister}
      />
    </div>
  );
};

export default NewDashboard;
